// src/padplanner/ChooseLibrary.jsx
//
// An unpinned plan's one-time choice of its organization's own published
// template library (PUT /plans/:id/template-set, D-S4-2). The AI PRIME
// standard set needs no pin and stays available (D-S4-1); the pin is final,
// and the confirm says so. Shared by the editor's palette and the plan
// page's Templates tab, so the two say and do the same.
import { useState } from "react";
import { pinTemplateSet } from "../api/padplannerApi";
import { PIN_FINAL_TEXT, NO_OWN_LIBRARY_TEXT } from "./editorPalette";
import { setChipLabel } from "./templateVocabulary";
import { refusalText } from "./planRefusals";

export const UNPINNED_TEXT = "This plan has no template library of its own. The AI PRIME standard library is available.";

/**
 * @param {object} p
 * @param {object} p.plan
 * @param {object[]} p.choosable  editorPalette.js paletteSets().choosable: the org's published sets
 * @param {boolean} [p.canPin]    the user may write the plan (EDIT); otherwise the choice is not offered
 * @param {(plan: object, setId: string) => void} p.onPinned  the server's plan after the pin
 */
export default function ChooseLibrary({ plan, choosable, canPin = true, onPinned, buttonClass = "op-btn op-btn--sm" }) {
  const [choosing, setChoosing] = useState(false);
  const [choice, setChoice] = useState(null);
  const [pinError, setPinError] = useState(null);

  async function pin() {
    const s = choosable.find((x) => x.id === choice);
    if (!s) return;
    if (!window.confirm(`Pin ${setChipLabel(s)} to ${plan.plan_number}? ${PIN_FINAL_TEXT}`)) return;
    setPinError(null);
    try {
      const r = await pinTemplateSet(plan.id, s.id);
      setChoosing(false);
      onPinned(r.plan, s.id);
    } catch (err) { setPinError(refusalText(err)); }
  }

  return (
    <div className="flex flex-col gap-2 rounded border border-bg-border bg-bg px-3 py-2.5" data-testid="pp-choose-library">
      <p className="op-meta">{UNPINNED_TEXT}</p>
      {choosable.length === 0 && <p className="op-meta">{NO_OWN_LIBRARY_TEXT}</p>}
      {canPin && choosable.length > 0 && !choosing && (
        <button type="button" className={`${buttonClass} self-start`} onClick={() => setChoosing(true)}>Choose library</button>
      )}
      {choosing && (
        <fieldset className="flex flex-col gap-1.5">
          <legend className="op-label uppercase">Your organization's libraries</legend>
          {choosable.map((s) => (
            <label key={s.id} className="flex items-center gap-2 text-[13px] text-fg-primary">
              <input type="radio" name="pp-library" value={s.id} checked={choice === s.id} onChange={() => setChoice(s.id)} />
              {setChipLabel(s)}
            </label>
          ))}
          <p className="op-meta">{PIN_FINAL_TEXT}</p>
          <div className="flex gap-2">
            <button type="button" className="op-btn op-btn--primary op-btn--sm" disabled={!choice} onClick={pin}>Pin library</button>
            <button type="button" className={buttonClass} onClick={() => { setChoosing(false); setChoice(null); }}>Cancel</button>
          </div>
          {pinError && <p className="text-sm text-accent-red" role="alert">{pinError}</p>}
        </fieldset>
      )}
    </div>
  );
}
